import React, { ChangeEvent, FormEvent, useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import apiService, { ContactPayload } from "../../services/api";
import PageShell, { PageHero } from "./PageShell";
import { Arrow, Display, Eyebrow } from "../components/Primitives";
import { pickLang } from "../copy";
import { route } from "../paths";

/**
 * B2B / "Entreprises" page. Same lead flow as v1 (POST /contact with a B2B
 * subject), re-set in the editorial system.
 */
const B2BPageV2: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const lang = pickLang(currentLanguage);
  const isFr = lang === "fr";

  const segments = isFr
    ? ["Conciergeries", "Hôtellerie", "Retail & revendeurs", "Clubs & salles de sport", "Avantages salariés", "Collectivités"]
    : ["Concierge services", "Hospitality", "Retail & resellers", "Clubs & gyms", "Employee perks", "Public orgs"];

  const steps = isFr
    ? [
        { img: "/illustrations/landing/collecte.webp", title: "On collecte", desc: "Sur site, partout en Île-de-France." },
        { img: "/illustrations/landing/nettoyage.webp", title: "On chouchoute", desc: "Nettoyage expert, paire par paire." },
        { img: "/illustrations/landing/livraison.webp", title: "On livre", desc: "Vos paires reviennent comme neuves." },
      ]
    : [
        { img: "/illustrations/landing/collecte.webp", title: "We collect", desc: "On-site, anywhere across Île-de-France." },
        { img: "/illustrations/landing/nettoyage.webp", title: "We pamper", desc: "Expert cleaning, pair by pair." },
        { img: "/illustrations/landing/livraison.webp", title: "We deliver", desc: "Your pairs come back like new." },
      ];

  const perks = isFr
    ? ["Collecte sur site", "Facturation mensuelle · 30 jours", "Express 48h / 24h", "Interlocuteur dédié"]
    : ["On-site pickup", "Monthly billing · 30 days", "Express 48h / 24h", "Dedicated contact"];

  const trust = isFr
    ? ["Entreprise immatriculée au RCS de Paris", "Atelier en Île-de-France", "Collecte & livraison en Île-de-France", "Un interlocuteur dédié à votre compte"]
    : ["Registered company (Paris Trade Register)", "Workshop in Île-de-France", "Pickup & delivery across Île-de-France", "A dedicated contact for your account"];

  const sectorOptions = isFr
    ? ["Conciergerie", "Hôtellerie", "Retail / revendeur", "Club / salle de sport", "Entreprise (avantages salariés)", "Collectivité", "Autre"]
    : ["Concierge service", "Hospitality", "Retail / reseller", "Club / gym", "Company (employee perks)", "Public organization", "Other"];

  const [form, setForm] = useState({ company: "", contact: "", email: "", phone: "", sector: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const isValidFRPhone = (v: string) => {
    const s = v.replace(/[ .-]/g, "");
    return !s || /^(0[1-9]\d{8}|\+33[1-9]\d{8})$/.test(s);
  };
  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const formValid =
    form.company.trim().length >= 2 && form.contact.trim().length >= 2 && isValidEmail(form.email) && isValidFRPhone(form.phone);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!formValid) {
      setError(isFr ? "Merci de renseigner la société, le contact et un email valide." : "Please fill in the company, contact and a valid email.");
      return;
    }
    setSubmitting(true);
    const details = isFr
      ? [`Société : ${form.company}`, form.sector && `Secteur : ${form.sector}`, "", form.message || "(Aucun message complémentaire)"]
      : [`Company: ${form.company}`, form.sector && `Sector: ${form.sector}`, "", form.message || "(No additional message)"];
    const payload: ContactPayload = {
      name: `${form.company} — ${form.contact}`,
      email: form.email,
      phone: form.phone || undefined,
      subject: isFr ? "Demande B2B / Entreprises" : "B2B / Business inquiry",
      message: details.filter(Boolean).join("\n"),
    };
    try {
      await apiService.submitContact(payload);
      setSuccess(
        isFr
          ? "Merci ! Votre demande a bien été envoyée, notre équipe B2B vous recontacte sous 24-48h."
          : "Thank you! Your request has been sent, our B2B team will get back to you within 24-48h.",
      );
      setForm({ company: "", contact: "", email: "", phone: "", sector: "", message: "" });
    } catch (err: any) {
      setError(err?.message || (isFr ? "Une erreur est survenue, réessayez plus tard." : "Something went wrong, please try again later."));
    } finally {
      setSubmitting(false);
    }
  };

  // Underlined editorial fields. 16px on mobile keeps iOS from zooming.
  const field =
    "w-full border-0 border-b border-ink/25 bg-transparent px-0 py-3 text-base text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-tangerine sm:text-[15px]";
  const label = "block font-mono text-[10px] uppercase tracking-[0.22em] text-ink/50";

  return (
    <PageShell
      seo={{
        title: isFr
          ? "Nettoyage de sneakers pour entreprises en Île-de-France | Lave Tes Baskets"
          : "Sneaker cleaning for businesses in Île-de-France | Lave Tes Baskets",
        description: isFr
          ? "Offre B2B pour conciergeries, hôtels, retail et clubs : collecte sur site en Île-de-France, nettoyage expert, facturation mensuelle. Devis au volume sous 24-48h."
          : "B2B offer for concierge services, hotels, retail and clubs: on-site pickup across Île-de-France, expert cleaning, monthly billing. Volume quote within 24-48h.",
        canonicalPath: route(lang, "entreprises"),
        jsonLd: [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: isFr ? "Offre B2B nettoyage de sneakers" : "B2B sneaker cleaning offer",
            provider: { "@type": "LocalBusiness", name: "Lave Tes Baskets" },
            areaServed: "Île-de-France",
          },
        ],
      }}
    >
      <PageHero
        crumbs={[
          { label: isFr ? "Accueil" : "Home", to: route(lang) },
          { label: isFr ? "Entreprises" : "Business" },
        ]}
        index="07"
        eyebrow={isFr ? "Entreprises · B2B" : "Business · B2B"}
        title={
          isFr ? (
            <>
              Vos sneakers, <em className="italic text-tangerine">nickel,</em> à l'échelle pro.
            </>
          ) : (
            <>
              Your sneakers, <em className="italic text-tangerine">spotless,</em> at pro scale.
            </>
          )
        }
        lede={
          isFr
            ? "Collecte sur site en Île-de-France, nettoyage expert, facturation mensuelle. On s'occupe du volume, vous rayonnez."
            : "On-site pickup across Île-de-France, expert cleaning, monthly billing. We handle the volume, you shine."
        }
        actions={
          <>
            <a href="#b2b-form" className="v2-btn v2-btn-solid">
              {isFr ? "Demander un devis pro" : "Request a business quote"}
              <Arrow />
            </a>
            <a href="#b2b-offer" className="v2-btn v2-btn-ghost">
              {isFr ? "Découvrir l'offre" : "Explore the offer"}
            </a>
          </>
        }
        aside={
          <img src="/illustrations/landing/entreprises.webp" alt="" loading="lazy" className="ml-auto w-full max-w-[460px] object-contain" />
        }
      />

      {/* partner + trust */}
      <section id="b2b-offer" className="bg-bone py-20 text-ink sm:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-4 sm:px-6 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Eyebrow index="01">{isFr ? "Ils nous font confiance" : "They trust us"}</Eyebrow>
            <Display className="mt-5 text-[clamp(2rem,4.6vw,4.25rem)]">
              {isFr
                ? "Circles (a sodexo company) nous confie déjà le soin des sneakers de ses clients."
                : "Circles (a sodexo company) already trusts us with their clients' sneakers."}
            </Display>
            <p className="v2-reveal mt-5 text-ink/60" data-delay="2">
              {isFr ? "Le même niveau d'exigence, à votre service." : "The same standard of care, at your service."}
            </p>
          </div>
          <div className="lg:col-span-6 lg:pt-10">
            {trust.map((tItem, i) => (
              <div key={i} className="v2-reveal grid grid-cols-[3rem_1fr] gap-4 border-t border-ink/15 py-5 last:border-b" data-delay={String(i + 1)}>
                <span className="pt-1 font-mono text-[11px] tracking-[0.22em] text-teal-deep">{`0${i + 1}`}</span>
                <span className="text-ink/80">{tItem}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* segments */}
      <section className="v2-hair border-t bg-ink py-20 text-bone sm:py-28">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <Eyebrow index="02">{isFr ? "Secteurs" : "Sectors"}</Eyebrow>
          <Display className="mt-5 max-w-3xl text-[clamp(2.2rem,5.5vw,5rem)]">
            {isFr ? "Pensé pour votre secteur." : "Built for your sector."}
          </Display>
          <div className="v2-reveal mt-10 flex flex-wrap gap-2" data-delay="2">
            {segments.map((s) => (
              <span key={s} className="rounded-full border border-bone/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors hover:border-teal hover:text-teal-light">
                {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 3 steps */}
      <section className="bg-bone py-20 text-ink sm:py-28">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <Eyebrow index="03">{isFr ? "Méthode" : "Method"}</Eyebrow>
          <Display className="mt-5 max-w-3xl text-[clamp(2.2rem,5.5vw,5rem)]">
            {isFr ? "Simple comme 1 · 2 · 3." : "As easy as 1 · 2 · 3."}
          </Display>
          <div className="mt-12 grid gap-px border border-ink/10 bg-ink/10 md:grid-cols-3">
            {steps.map((s, i) => (
              <div key={i} className="v2-reveal flex flex-col bg-bone p-6 sm:p-8" data-delay={String(i + 1)}>
                <div className="font-mono text-[11px] tracking-[0.22em] text-teal-deep">{`0${i + 1}`}</div>
                <img src={s.img} alt="" loading="lazy" className="my-6 h-44 w-full object-contain sm:h-52" />
                <h3 className="font-display text-3xl leading-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-ink/60">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="v2-reveal mt-8 flex flex-wrap gap-2" data-delay="2">
            {perks.map((p) => (
              <span key={p} className="rounded-full border border-ink/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em]">
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* lead form */}
      <section className="bg-ink py-20 text-bone sm:py-28">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <div id="b2b-form" className="v2-reveal grid scroll-mt-28 overflow-hidden rounded-2xl bg-bone text-ink lg:grid-cols-12">
            <div className="flex flex-col p-7 sm:p-12 lg:col-span-5">
              <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-ink/60">
                <span className="h-2 w-2 rounded-full bg-teal" />
                {isFr ? "Devis sur mesure" : "Tailored quote"}
              </div>
              <h2 className="mt-6 font-display text-[clamp(2rem,4.5vw,4rem)] leading-[0.98] tracking-[-0.02em]">
                {isFr ? "Parlons de votre volume." : "Let's talk about your volume."}
              </h2>
              <p className="mt-4 max-w-sm text-ink/65">
                {isFr
                  ? "Votre secteur, vos paires, votre rythme : on établit une grille dédiée et on organise la collecte."
                  : "Your sector, your pairs, your pace: we build a dedicated rate card and organize pickup."}
              </p>
              <ul className="mt-8 space-y-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/60">
                {(isFr
                  ? ["Réponse sous 24 – 48h", "Sans engagement", "Collecte sur toute l'Île-de-France"]
                  : ["Reply within 24 – 48h", "No commitment", "Pickup across Île-de-France"]
                ).map((x) => (
                  <li key={x} className="flex items-center gap-3">
                    <span className="h-px w-4 bg-tangerine" />
                    {x}
                  </li>
                ))}
              </ul>
              <img src="/illustrations/landing/entreprises.webp" alt="" loading="lazy" className="mt-auto hidden h-56 w-full object-contain object-left pt-8 lg:block" />
            </div>

            <div className="border-t border-ink/10 p-7 sm:p-12 lg:col-span-7 lg:border-l lg:border-t-0">
              {success ? (
                <div className="flex h-full flex-col items-start justify-center py-10">
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-teal-deep">{`✓ ${isFr ? "Envoyé" : "Sent"}`}</div>
                  <p className="mt-4 max-w-md font-display text-2xl leading-tight">{success}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-7">
                  <div className="grid gap-7 sm:grid-cols-2">
                    <div>
                      <label className={label} htmlFor="b2b-company">{isFr ? "Société *" : "Company *"}</label>
                      <input id="b2b-company" name="company" value={form.company} onChange={handleChange} className={field} required />
                    </div>
                    <div>
                      <label className={label} htmlFor="b2b-contact">{isFr ? "Nom du contact *" : "Contact name *"}</label>
                      <input id="b2b-contact" name="contact" value={form.contact} onChange={handleChange} className={field} required />
                    </div>
                  </div>
                  <div className="grid gap-7 sm:grid-cols-2">
                    <div>
                      <label className={label} htmlFor="b2b-email">Email *</label>
                      <input id="b2b-email" name="email" type="email" value={form.email} onChange={handleChange} className={field} required />
                    </div>
                    <div>
                      <label className={label} htmlFor="b2b-phone">{isFr ? "Téléphone" : "Phone"}</label>
                      <input id="b2b-phone" name="phone" value={form.phone} onChange={handleChange} className={field} />
                    </div>
                  </div>
                  <div>
                    <label className={label} htmlFor="b2b-sector">{isFr ? "Secteur" : "Sector"}</label>
                    <select id="b2b-sector" name="sector" value={form.sector} onChange={handleChange} className={`${field} appearance-none`}>
                      <option value="">{isFr ? "Sélectionner" : "Select"}</option>
                      {sectorOptions.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={label} htmlFor="b2b-message">{isFr ? "Votre besoin" : "Your needs"}</label>
                    <textarea
                      id="b2b-message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={3}
                      className={field}
                      placeholder={isFr ? "Décrivez brièvement votre besoin, vos délais…" : "Briefly describe your need, your deadlines…"}
                    />
                  </div>
                  {error && <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-tangerine">{error}</p>}
                  <button type="submit" disabled={submitting} className="v2-btn v2-btn-ink w-full justify-center disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                    {submitting ? (isFr ? "Envoi…" : "Sending…") : isFr ? "Envoyer ma demande" : "Send my request"}
                    <Arrow />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default B2BPageV2;
